/**
 * The shopper's public username: the hook the account page's dialog saves through, and the
 * message for each way a save can fail.
 */
import { type EndUser, LootApiError } from "@loot-os/client";
import { useMutation } from "@tanstack/react-query";

import { qk } from "./queries.js";
import { queryClient } from "./queryClient.js";
import { useShopper } from "./shopper.js";

/** What to tell the shopper when saving a username failed. */
export function usernameErrorMessage(err: unknown): string {
  if (err instanceof LootApiError) {
    switch (err.code) {
      case "username_taken":
        return "That username is taken. Try another.";
      case "rate_limited":
        return "Too many attempts. Wait a minute and try again.";
      default:
        return err.message || "Could not save the username.";
    }
  }
  return err instanceof Error && err.message ? err.message : "Could not save the username.";
}

/**
 * Saves the username; GET /v1/me is replaced with the answer at once. Cached battles still name
 * the seat by the old one until their next fetch, so the battle lists are invalidated too.
 */
export function useSaveUsername() {
  const { client } = useShopper();
  return useMutation({
    mutationFn: (username: string): Promise<EndUser> => client.updateMyUsername(username),
    onSuccess: (user) => {
      queryClient.setQueryData(qk.lootMe, user);
      void queryClient.invalidateQueries({ queryKey: ["battles", "list"] });
      void queryClient.invalidateQueries({ queryKey: qk.myBattles });
    },
  });
}
